<template>
  <svg :width="`${widthInInches}in`" :height="`${heightInInches}in`" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <!-- Define a mask using the PNG -->
      <mask :id="`${uuid}-pngMask`">
        <!-- Use the PNG image to define the mask -->
        <image
          :href="imageUrl"
          :x="artworkOffsetX"
          :y="artworkOffsetY"
          :width="artworkWidthInInches"
          :height="artworkHeightInInches"
          preserveAspectRatio="none"
        />
      </mask>

      <!-- Define a filter for the drop shadow -->
      <filter :id="`${uuid}-dropShadow`" x="0" y="0" :width="widthInInches" :height="heightInInches" filterUnits="userSpaceOnUse">
        <feGaussianBlur in="SourceAlpha" stdDeviation="0.1" />
        <feOffset dx="-0.1" dy="0.1" result="offsetblur" />
        <feMerge>
          <feMergeNode in="offsetblur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    <!-- Backing board component -->
    <RenderSvgBackingBoard
      :frameSpec="frameSpec"
      :widthInInches="widthInInches"
      :heightInInches="heightInInches"
      :x="0"
      :y="0"
    />
    

    <!-- Apply the mask and drop shadow to the shape -->
    <g v-if="elevatedMount" :style="`filter: url(#${uuid}-dropShadow)`">
      <rect
        v-if="imageIsPng"
        :x="artworkOffsetX"
        :y="artworkOffsetY"
        :width="artworkWidthInInches"
        :height="artworkHeightInInches"
        :mask="`url(#${uuid}-pngMask)`"
        fill="rgb(0, 0, 0, 0.5)"
      />
      <rect
        v-else
        :x="artworkOffsetX"
        :y="artworkOffsetY"
        :width="artworkWidthInInches"
        :height="artworkHeightInInches"
        fill="rgb(0, 0, 0, 0.5)"
      />
    </g>

    <!-- Artwork component -->
    <RenderSvgArtwork
      :frameSpec="frameSpec"
      :x="artworkOffsetX"
      :y="artworkOffsetY"
      :allowUpload="allowUpload"
      @upload-click="$emit('upload-click')"
    />

    <!-- Mat components -->
    <RenderSvgMat
      v-for="(mat, index) in frameSpec.mats.slice().reverse()"
      :key="index"
      :frameSpec="frameSpec"
      :x="0"
      :y="0"
      :mat="mat"
      :matIndex="index"
    />
  </svg>
</template>

<script>
import RenderSvgArtwork from './RenderSvgArtwork.vue';
import RenderSvgMat from './RenderSvgMat.vue';
import RenderSvgBackingBoard from './RenderSvgBackingBoard.vue';
import { defaultMatThickness } from "../../hardCodedValues";
import isEmpty from "lodash/isEmpty";
import uuidv4 from "uuid/v4";

export default {
  components: {
    RenderSvgArtwork,
    RenderSvgMat,
    RenderSvgBackingBoard
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    allowUpload: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    uuid() {
      return uuidv4();
    },
    artwork() {
      return this.frameSpec.getArtwork() || {};
    },
    elevatedMount() {
      return this.frameSpec.mount.elevated;
    },
    mat() {
      return this.frameSpec.mats[0] || {};
    },
    imageIsPng() {
      if (!this.frameSpec) return "";

      return this.artwork.customerSpecified && this.artwork.customerSpecified.url.includes(".png");
    },
    imageIsJpg() {
      if (!this.frameSpec) return "";

      return this.artwork.customerSpecified && this.artwork.customerSpecified.url.includes(".jpeg");
    },
    imageUrl() {
      if (!this.frameSpec) return "";

      if ((this.imageIsPng && this.imageIsJpg) || !this.artwork.customerSpecified) {
        return this.artwork.croppedAndNormalized.url;
      } else {
        return this.artwork.customerSpecified.url;
      }
    },
    isCanvasMoulding() {
      return this.frameSpec.isCanvas();
    },
    totalsOfAllMatRevealsOrMountReveals() {
      if (!isEmpty(this.mat)) {
        return {
          topInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
          rightInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
          bottomInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
          leftInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
        };
      } else if (this.frameSpec.isFloated() || this.frameSpec.isClearFloated()) {
        return this.frameSpec.mount.reveal;
      } else {
        return {
          topInInches: 0,
          rightInInches: 0,
          bottomInInches: 0,
          leftInInches: 0
        };
      }
    },
    widthInInches() {
      if (!this.artwork.exterior) return '0';
      return this.artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatRevealsOrMountReveals.leftInInches + this.totalsOfAllMatRevealsOrMountReveals.rightInInches;
    },
    heightInInches() {
      if (!this.artwork.exterior) return '0';
      return this.artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatRevealsOrMountReveals.topInInches + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches;
    },
    artworkWidthInInches() {
      return this.artwork.exterior.widthInInches;
    },
    artworkHeightInInches() {
      return this.artwork.exterior.heightInInches;
    },
    artworkOffsetX() {
      if (this.isCanvasMoulding) {
        return this.totalsOfAllMatRevealsOrMountReveals.leftInInches;
      } else {
        return this.totalsOfAllMatRevealsOrMountReveals.leftInInches - defaultMatThickness;
      }
    },
    artworkOffsetY() {
      if (this.isCanvasMoulding) {
        return this.totalsOfAllMatRevealsOrMountReveals.topInInches;
      } else {
        return this.totalsOfAllMatRevealsOrMountReveals.topInInches - defaultMatThickness;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
svg {
  align-items: center;
  display: flex;
  height: auto;
  justify-content: center;
  max-width: 100%;
  width: 100%;
}
</style>